<template>
  <div>
    <!-- Status Selector (when isPost) -->
    <div class="flex flex-row" v-if="isPost" id="selected-status">
      <div class="relative mr-2 flex-1" id="edit-status-name">
        <select
          id="status"
          name="selectedValue"
          v-model="selectedValue"
          class="select capitalize"
        >
          <option
            v-for="option in showAllStatuses"
            :key="option.id"
            :value="option.id"
            class="capitalize"
          >
            {{ option.name }}
          </option>
        </select>
      </div>
      <div class="text-center" id="edit-change-status-btn">
        <button
          type="button"
          class="btn-main"
          :disabled="isSaving || !hasChanges"
          @click.prevent="changepostStatus"
        >
          <div class="flex items-center justify-center gap-2">
            <svg v-if="isSaving" class="spinner" stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" height="16px" width="16px" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 6l0 -3"></path>
              <path d="M16.25 7.75l2.15 -2.15"></path>
              <path d="M18 12l3 0"></path>
              <path d="M16.25 16.25l2.15 2.15"></path>
              <path d="M12 18l0 3"></path>
              <path d="M7.75 16.25l-2.15 2.15"></path>
              <path d="M6 12l-3 0"></path>
              <path d="M7.75 7.75l-2.15 -2.15"></path>
            </svg>
            {{ isSaving ? $t('Saving...') : $t('Save') }}
          </div>
        </button>
      </div>
    </div>

    <!-- "Not now" warning -->
    <transition name="fadeIn">
      <div
        v-if="selectedStatusValue && selectedStatusValue.name === 'Not now'"
        class="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl"
      >
        <div class="flex items-start gap-2.5">
          <div class="flex-shrink-0 mt-0.5">
            <svg class="w-4 h-4 text-amber-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          </div>
          <div class="text-sm text-amber-900">
            <p class="font-medium">{{ $t('Users will not be notified of this change.') }}</p>
            <p class="mt-1 text-amber-700">
              {{ $t('Ideas marked "Not Now" are hidden from the default board view.') }}
            </p>
          </div>
        </div>
      </div>
    </transition>

    <!-- Edit Statuses Drawer -->
    <div class="relative" id="edit-status-label">
      <transition name="fadeIn">
        <div
          class="drawer-backdrop"
          v-if="labelsOpen"
          @click.self="labelsOpen = false"
        ></div>
      </transition>

      <transition name="slideFromRight">
        <div class="drawer" v-if="labelsOpen">
          <!-- Header -->
          <div class="drawer__header">
            <div class="drawer__header-content">
              <h3 class="drawer__title">{{ $t('Edit Statuses') }}</h3>
              <p class="drawer__subtitle">{{ $t('Reorder, rename, or create new statuses') }}</p>
            </div>
            <button
              type="button"
              @click.prevent="labelsOpen = false"
              class="drawer__close"
              :title="$t('Close')"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="drawer__body">
            <p class="text-sm text-neutral-600 mb-4">
              {{ $t('Drag to reorder. Click a status to edit its name or color.') }}
            </p>

            <div class="drawer__list">
              <draggable
                @change="sortingStatus"
                :list="filteredStatus"
                :animation="200"
                ghost-class="moving-card"
                filter=".action-button"
                class="w-full"
                @start="drag = true"
                @end="drag = false"
                :options="{ handle: '.handle' }"
              >
                <transition-group>
                  <StatusLabel
                    v-for="label in filteredStatus"
                    :original-label="label"
                    :key="label.id"
                    :project-id="projectId"
                    :status-name="statusName"
                    @destroyedLabel="removeLabel($event)"
                    @updatedLabel="updateLabel($event)"
                  />
                </transition-group>
              </draggable>

              <NewStatusLabel
                :project-id="projectId"
                :status-name="statusName"
                @newLabel="addLabel($event)"
              />
            </div>
          </div>

          <!-- Success Toast -->
          <transition name="fadeIn">
            <div v-if="successMessage" class="drawer__toast">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
              {{ $t("Changes saved") }}
            </div>
          </transition>
        </div>
      </transition>

      <!-- Open Drawer Button -->
      <button
        v-if="!is_moderator"
        :class="
          button_status
            ? 'w-full focus:outline-none text-sm bg-white border border-neutral-300 rounded-lg bg-gradient-to-b from-white to-neutral-100 shadow-sm px-3 py-1 text-dblue-500 flex items-center focus:to-white focus:border-neutral-400'
            : 'focus:outline-none btn-flat w-full mt-2'
        "
        @click.prevent="getStatuses"
      >
        <svg
          width="16px"
          height="16px"
          class="mr-2"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          v-if="!button_status"
        >
          <path
            fill="currentColor"
            d="M7,6H6V3A1,1,0,0,0,4,3V6H3A1,1,0,0,0,3,8H7A1,1,0,0,0,7,6ZM5,10a1,1,0,0,0-1,1V21a1,1,0,0,0,2,0V11A1,1,0,0,0,5,10Zm7,8a1,1,0,0,0-1,1v2a1,1,0,0,0,2,0V19A1,1,0,0,0,12,18Zm9-8H20V3a1,1,0,0,0-2,0v7H17a1,1,0,0,0,0,2h4a1,1,0,0,0,0-2Zm-2,4a1,1,0,0,0-1,1v6a1,1,0,0,0,2,0V15A1,1,0,0,0,19,14Zm-5,0H13V3a1,1,0,0,0-2,0V14H10a1,1,0,0,0,0,2h4a1,1,0,0,0,0-2Z"
          />
        </svg>
        <span>{{ $t('Edit Statuses') }}</span>
      </button>
    </div>

    <ChangelogAi
      :show-company-details="this.showCompanyDetails"
      :show-changelog-post-suggestion="this.showChangelogPostSuggestion"
      :post="this.post"
      :board="this.board"
      :project="this.project"
      :token="this.token"
      @dismissed="changelogDismissed = true"
    />
  </div>
</template>

<script>
import StatusLabel from "./status_label";
import NewStatusLabel from "./new_status_label";
import EventBus from "event_bus";
import draggable from "vuedraggable";
import ChangelogModal from "./changelog_modal";
import ChangelogAi from "./changelog_ai";

export default {
  props: [
    "labels",
    "project-id",
    "button_status",
    "selected-status",
    "post",
    "board",
    "status-ids",
    "status-name",
    "allow-priority-change",
    "is-post",
    "changelog-suggestions",
    "project",
    "token",
    "subscription_valid",
    "is_moderator"
  ],

  data: function() {
    return {
      allStatuses: this.labels,
      labelsOpen: false,
      selectedValue: this.selectedStatus ? this.selectedStatus.id : null,
      savedValue: this.selectedStatus ? this.selectedStatus.id : null,
      isSaving: false,
      filteredStatus: [],
      statusId: this.statusIds,
      successMessage: false,
      showChangelogPostSuggestion: false,
      showCompanyDetails: false,
      changelogDismissed: false
    };
  },

  mounted: function() {
    this.keydownHandler = e => {
      if (e.key === "Escape") {
        this.labelsOpen = false;
      }
    };
    window.addEventListener("keydown", this.keydownHandler);
    this.selectedValuetSet = this.selectedValuetSet();
    this.filteredStatus = this.filterByNotSet();

    this.statusLabelHandler = status => {
      this.allStatuses = status;
    };
    EventBus.$on("status_label", this.statusLabelHandler);
  },

  components: {
    StatusLabel,
    NewStatusLabel,
    draggable,
    ChangelogModal,
    ChangelogAi
  },

  computed: {
    showAllStatuses() {
      const filterAllStatus = this.statusId.map(id => {
        return this.allStatuses.find(status => status.id === id);
      });
      const notSet = this.allStatuses.filter(
        status => status.name === this.statusName.not_set
      );
      filterAllStatus.push(notSet[0]);
      return filterAllStatus;
    },

    selectedStatusValue() {
      return this.showAllStatuses.find(
        status => status && status.id === this.selectedValue
      );
    },

    hasChanges() {
      return this.selectedValue !== this.savedValue;
    }
  },

  methods: {
    getStatuses() {
      Rails.ajax({
        url: "/statuses?project_id=" + this.projectId,
        type: "GET",
        dataType: "json",
        beforeSend: function() {
          return true;
        },
        success: data => {
          this.allStatuses = data.status_label;
          this.statusId = data.status_ids;
        },
        error: (data, status) => {}
      });
      this.labelsOpen = !this.labelsOpen;
    },

    filterByNotSet() {
      const result = this.allStatuses.filter(
        status => status.name !== this.statusName.not_set
      );
      const filterStatus = this.statusId.map(id => {
        return result.find(status => status.id === id);
      });
      return filterStatus;
    },

    selectedValuetSet() {
      let result = this.allStatuses.filter(
        status => status.id === (this.selectedStatus?.id || "")
      );
      if (result.length) return this.selectedStatus.id;
      result = this.allStatuses.filter(
        status => status.name === this.statusName.not_set
      );
      return result[0].id;
    },

    changepostStatus() {
      if (this.isSaving || !this.hasChanges) return;
      this.isSaving = true;
      // Keep the saving state visible for at least a second so the feedback registers
      const saveStartedAt = Date.now();
      const afterMinimumDelay = callback => {
        setTimeout(callback, Math.max(0, 1000 - (Date.now() - saveStartedAt)));
      };
      let params = new FormData();
      if (this.subscription_valid === "true") {
        this.showAllStatuses.forEach(status => {
          if (status && status.id === this.selectedValue) {
            this.savedStatus = status;
          }
        });
        if (this.savedStatus && "Done" === this.savedStatus.name && this.changelogSuggestions && !this.changelogDismissed) {
          if (!this.project.company_details) {
            this.showCompanyDetails = true;
            this.showChangelogPostSuggestion = false;
          } else {
            this.showCompanyDetails = false;
            this.showChangelogPostSuggestion = true;
          }
          params.append("check_json", true);
        }
      }

      params.append("post[status_id]", this.selectedValue);

      Rails.ajax({
        url: "/boards/" + this.board.slug + "/posts/" + this.post.slug,
        type: "PATCH",
        data: params,
        dataType: "json",
        beforeSend: function() {
          return true;
        },
        success: data => {
          afterMinimumDelay(() => {
            this.isSaving = false;
            this.savedValue = this.selectedValue;
            const selected = this.showAllStatuses.find(
              s => s && s.id === this.selectedValue
            );
            if (selected) {
              EventBus.$emit("post_status_changed", {
                name: selected.name,
                color: selected.color,
                disable_votes: selected.disable_votes
              });
            }
          });
        },
        error: (data, status) => {
          afterMinimumDelay(() => {
            this.isSaving = false;
            console.log(data);
          });
        }
      });
    },

    addLabel(label) {
      this.allStatuses = label.status_label;
      this.statusId = label.status_ids;
      this.selectedValue = this.selectedValuetSet();
      EventBus.$emit("status_label", label.status_label);
      EventBus.$emit("status_ids", label.status_ids);
      this.showSuccess();
    },

    removeLabel(label) {
      this.allStatuses = label.status_label;
      this.statusId = label.status_ids;
      this.selectedValue = this.selectedValuetSet();
      EventBus.$emit("status_label", label.status_label);
      EventBus.$emit("status_ids", label.status_ids);
      this.showSuccess();
    },

    updateLabel(label) {
      this.selectedValue = this.selectedValuetSet();
      EventBus.$emit("status_label", label);
      this.showSuccess();
    },

    showSuccess() {
      this.successMessage = true;
      setTimeout(() => {
        this.successMessage = false;
      }, 2000);
    },

    sortingStatus() {
      const statusIds = this.filteredStatus.map(status => {
        return status.id;
      });
      let params = new FormData();

      params.append("status[project_id]", this.projectId);
      params.append("status[status_ids]", statusIds);

      Rails.ajax({
        url: "/statuses/filter_status",
        type: "POST",
        data: params,
        dataType: "json",
        beforeSend: function() {
          return true;
        },
        success: data => {
          this.statusId = data.status_id;
          this.showSuccess();
        },
        error: (data, status) => {
          console.log(data);
        }
      });
    }
  },

  watch: {
    allStatuses: function() {
      this.filteredStatus = this.filterByNotSet();
    }
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.keydownHandler);
    EventBus.$off("status_label", this.statusLabelHandler);
  }
};
</script>

<style scoped>
/* Drawer styles are global */

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
